// src/pages/Transactions.jsx
import React, { useState, useMemo } from 'react'
import { useApp } from '../context/AppContext'
import {
  MetricsGrid, MetricCard, CatPill, AcctChip, TypeBadge,
  SearchInput, FilterPills, EmptyState, ConfirmModal, SectionHead,
} from '../components/UI'
import { INC_CATEGORIES, EXP_CATEGORIES } from '../utils/constants'
import { fmt, fmtDate, today } from '../utils/helpers'

const emptyForm = (type = 'expense') => ({
  date: today(), type, cat: '', acct: '', from: '', to: '', amt: '', note: '',
})

export default function Transactions() {
  const { accounts, transactions, addTransaction, updateTransaction, deleteTransaction, acctName, acctColor, totalIncome, totalExpense, netSavings } = useApp()

  const [txnType, setTxnType]   = useState('expense')
  const [form,    setForm]      = useState(emptyForm('expense'))
  const [editId,  setEditId]    = useState(null)
  const [search,  setSearch]    = useState('')
  const [filter,  setFilter]    = useState('All')
  const [delId,   setDelId]     = useState(null)
  const [formErr, setFormErr]   = useState('')

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const cats = txnType === 'income' ? INC_CATEGORIES : txnType === 'transfer' ? [] : EXP_CATEGORIES

  const setType = (t) => {
    setTxnType(t)
    setForm(emptyForm(t))
    setFormErr('')
  }

  const openEdit = (t) => {
    setTxnType(t.type)
    setForm({ date: t.date, type: t.type, cat: t.cat || '', acct: t.acct || '', from: t.from || '', to: t.to || '', amt: String(t.amt), note: t.note || '' })
    setEditId(t.id)
    setFormErr('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const cancelEdit = () => {
    setEditId(null)
    setForm(emptyForm(txnType))
    setFormErr('')
  }

  const validate = () => {
    if (!form.date) return 'Select a date'
    const amt = parseFloat(form.amt)
    if (!amt || amt <= 0) return 'Enter a valid amount'
    if (form.type === 'transfer') {
      if (!form.from || !form.to) return 'Select from and to accounts'
      if (form.from === form.to)  return 'From and To accounts must be different'
    } else {
      if (!form.cat)  return 'Select a category'
      if (!form.acct) return 'Select an account'
    }
    return ''
  }

  const save = () => {
    const err = validate()
    if (err) { setFormErr(err); return }
    const data = { ...form, type: txnType, amt: parseFloat(form.amt) }
    if (editId) updateTransaction(editId, data)
    else        addTransaction(data)
    setEditId(null)
    setForm(emptyForm(txnType))
    setFormErr('')
  }

  const filtered = useMemo(() =>
    transactions
      .filter(t => {
        const ms = (t.note || '').toLowerCase().includes(search.toLowerCase())
               || (t.cat  || '').toLowerCase().includes(search.toLowerCase())
               || t.ref.includes(search)
        const mf = filter === 'All' || t.type === filter.toLowerCase()
        return ms && mf
      })
      .sort((a, b) => b.date.localeCompare(a.date) || b.ref.localeCompare(a.ref)),
    [transactions, search, filter]
  )

  const acctOpts = accounts.map(a => (
    <option key={a.id} value={a.id}>{a.name}</option>
  ))

  return (
    <>
      {/* Summary metrics */}
      <MetricsGrid>
        <MetricCard label="Total income"   value={`₹${fmt(totalIncome)}`}   color="var(--green-600)" />
        <MetricCard label="Total expenses" value={`₹${fmt(totalExpense)}`}  color="var(--red-600)" />
        <MetricCard label="Net savings"    value={`${netSavings >= 0 ? '+' : '−'}₹${fmt(Math.abs(netSavings))}`} color={netSavings >= 0 ? 'var(--brand-600)' : 'var(--red-600)'} />
        <MetricCard label="Transactions"   value={String(transactions.length)} color="var(--gray-800)" />
      </MetricsGrid>

      {/* Add / Edit form */}
      <div className="card" style={{ marginBottom: 14 }}>
        <SectionHead icon="ti-plus" title={editId ? 'Edit transaction' : 'Add transaction'} />

        {/* Type switcher */}
        <div className="seg-control">
          <button className={`seg-btn income${txnType === 'income' ? ' active' : ''}`}   onClick={() => setType('income')}>
            <i className="ti ti-trending-up" aria-hidden="true" /> Income
          </button>
          <button className={`seg-btn expense${txnType === 'expense' ? ' active' : ''}`} onClick={() => setType('expense')}>
            <i className="ti ti-trending-down" aria-hidden="true" /> Expense
          </button>
          <button className={`seg-btn transfer${txnType === 'transfer' ? ' active' : ''}`} onClick={() => setType('transfer')}>
            <i className="ti ti-arrows-right-left" aria-hidden="true" /> Transfer
          </button>
        </div>

        {/* Form fields */}
        <div className="form-grid">
          <div className="field">
            <label className="field-label">Date</label>
            <input className="input" type="date" value={form.date} onChange={e => upd('date', e.target.value)} />
          </div>

          {txnType === 'transfer' ? (<>
            <div className="field">
              <label className="field-label">From account</label>
              <select className="select" value={form.from} onChange={e => upd('from', e.target.value)}>
                <option value="">Select…</option>{acctOpts}
              </select>
            </div>
            <div className="field">
              <label className="field-label">To account</label>
              <select className="select" value={form.to} onChange={e => upd('to', e.target.value)}>
                <option value="">Select…</option>{acctOpts}
              </select>
            </div>
          </>) : (<>
            <div className="field">
              <label className="field-label">Category</label>
              <select className="select" value={form.cat} onChange={e => upd('cat', e.target.value)}>
                <option value="">Select…</option>
                {cats.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="field-label">Account</label>
              <select className="select" value={form.acct} onChange={e => upd('acct', e.target.value)}>
                <option value="">Select…</option>{acctOpts}
              </select>
            </div>
          </>)}

          <div className="field">
            <label className="field-label">Amount (₹)</label>
            <input className="input" type="number" min="0" step="0.01" value={form.amt}
              onChange={e => upd('amt', e.target.value)} placeholder="0.00"
              style={{ textAlign: 'right' }} />
          </div>

          <div className="field col-span-2">
            <label className="field-label">Note / description</label>
            <textarea className="textarea" value={form.note} onChange={e => upd('note', e.target.value)} placeholder="Optional description…" />
          </div>
        </div>

        {formErr && (
          <div style={{ color: 'var(--red-600)', fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
            <i className="ti ti-alert-circle" aria-hidden="true" />{formErr}
          </div>
        )}

        <div className="btn-row">
          <button className="btn btn-primary" onClick={save}>
            <i className={`ti ti-${editId ? 'device-floppy' : 'check'}`} aria-hidden="true" />
            {editId ? 'Update' : 'Save'} transaction
          </button>
          {editId && (
            <button className="btn btn-danger" onClick={cancelEdit}>
              <i className="ti ti-x" aria-hidden="true" /> Cancel
            </button>
          )}
        </div>
      </div>

      {/* Transaction list */}
      <div className="table-wrap">
        <div className="table-toolbar">
          <span className="table-toolbar-title">All transactions ({filtered.length})</span>
          <SearchInput value={search} onChange={setSearch} />
          <FilterPills options={['All', 'Income', 'Expense', 'Transfer']} value={filter} onChange={setFilter} />
        </div>

        {filtered.length === 0 ? (
          <EmptyState icon="ti-list" message="No transactions found" sub="Add your first transaction using the form above" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Date</th>
                  <th style={{ width: 72 }}>Ref</th>
                  <th style={{ width: 80 }}>Type</th>
                  <th style={{ width: 120 }}>Category</th>
                  <th style={{ width: 145 }}>Account</th>
                  <th style={{ width: 105, textAlign: 'right' }}>Amount</th>
                  <th style={{ width: 68 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => {
                  const isTrf = t.type === 'transfer'
                  const amtEl = isTrf
                    ? <span className="c-transfer">⇄ ₹{fmt(t.amt)}</span>
                    : t.type === 'income'
                      ? <span className="c-income">+₹{fmt(t.amt)}</span>
                      : <span className="c-expense">−₹{fmt(t.amt)}</span>
                  return (
                    <tr key={t.id}>
                      <td className="mono">{fmtDate(t.date)}</td>
                      <td className="mono">{t.ref}</td>
                      <td><TypeBadge type={t.type} /></td>
                      <td><CatPill cat={t.cat} /></td>
                      <td>
                        {isTrf
                          ? <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>{acctName(t.from)} → {acctName(t.to)}</span>
                          : <AcctChip color={acctColor(t.acct)} name={acctName(t.acct)} />
                        }
                      </td>
                      <td style={{ textAlign: 'right' }}>{amtEl}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button className="icon-btn" onClick={() => openEdit(t)} aria-label="Edit transaction">
                            <i className="ti ti-edit" aria-hidden="true" />
                          </button>
                          <button className="icon-btn danger" onClick={() => setDelId(t.id)} aria-label="Delete transaction">
                            <i className="ti ti-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!delId}
        onClose={() => setDelId(null)}
        onConfirm={() => deleteTransaction(delId)}
        title="Delete transaction"
        message="This will permanently remove the transaction and reverse the account balance."
        confirmLabel="Delete"
      />
    </>
  )
}
